import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Surface, Button } from '@heroui/react'
import { useAuth } from '../../contexts/AuthContext'
import Check from '~icons/solar/check-circle-bold'
import DangerCircle from '~icons/solar/danger-circle-bold'
import TrendingUp from '~icons/solar/graph-up-bold'
import LoadingSpinner from '~icons/svg-spinners/270-ring-with-bg'

type Step = 'verifying' | 'success' | 'error'

export default function VerifyEmailPage() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [step, setStep] = useState<Step>('verifying')
  const [error, setError] = useState('')

  useEffect(() => {
    if (!token) {
      setStep('error')
      setError('This link is missing its verification token.')
      return
    }

    const verify = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-affiliate-email`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
          }
        )

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.error || 'Failed to verify your email')
        }

        setStep('success')
        setTimeout(() => {
          navigate(isAuthenticated ? '/affiliate/dashboard' : '/auth')
        }, 2000)
      } catch (err: any) {
        setStep('error')
        setError(err.message || 'Something went wrong. Please try again.')
      }
    }

    verify()
    // Runs once per token, on mount. isAuthenticated and navigate are
    // stable enough for this one-shot redirect and don't need to retrigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-accent rounded-xl mb-4">
            <TrendingUp className="h-8 w-8 text-accent-foreground" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {step === 'verifying' && 'Confirming your email'}
            {step === 'success' && 'Email confirmed'}
            {step === 'error' && 'Verification failed'}
          </h1>
        </div>

        <Surface variant="default" className="rounded-2xl p-8 text-center space-y-6">
          {step === 'verifying' && (
            <div className="flex flex-col items-center gap-4">
              <LoadingSpinner className="h-8 w-8 text-accent" />
              <p className="text-muted">One moment...</p>
            </div>
          )}

          {step === 'success' && (
            <>
              <div className="w-16 h-16 bg-success-soft rounded-full flex items-center justify-center mx-auto">
                <Check className="h-8 w-8 text-success-soft-foreground" />
              </div>
              <p className="text-muted">
                {isAuthenticated ? 'Taking you to your dashboard...' : 'Taking you to sign in...'}
              </p>
            </>
          )}

          {step === 'error' && (
            <>
              <div className="w-16 h-16 bg-danger-soft rounded-full flex items-center justify-center mx-auto">
                <DangerCircle className="h-8 w-8 text-danger-soft-foreground" />
              </div>
              <p className="text-muted">{error}</p>
              <Button variant="ghost" className="w-full" onPress={() => navigate('/auth')}>
                Back to Sign In
              </Button>
            </>
          )}
        </Surface>
      </motion.div>
    </div>
  )
}
